import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getTableCellNodeFromLexicalNode,
  $isTableCellNode,
  $isTableSelection,
  TableSelection,
} from "@lexical/table";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from "lexical";
import { useEffect } from "react";

/**
 * Clears the content of all selected table cells while preserving cell structure.
 * Each cell is emptied and given a fresh empty paragraph.
 */
const $clearSelectedCells = (selection: TableSelection): void => {
  const nodes = selection.getNodes();
  nodes.forEach((node) => {
    if ($isTableCellNode(node)) {
      // Clear the cell content but preserve the cell structure
      const children = node.getChildren();
      children.forEach((child) => child.remove());

      // Add an empty paragraph to maintain cell structure
      const paragraph = $createParagraphNode();
      node.append(paragraph);
    }
  });
};

function TableCellSelectionPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Handle Delete key when table cells are selected
    const unregisterDelete = editor.registerCommand(
      KEY_DELETE_COMMAND,
      () => {
        const selection = $getSelection();

        // Handle table selection (multiple cells selected)
        if ($isTableSelection(selection)) {
          $clearSelectedCells(selection);
          return true;
        }

        // For range selection within a single cell, let default behavior handle it
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const cellNode = $getTableCellNodeFromLexicalNode(anchorNode);

          if (cellNode) {
            // Let the default delete behavior work within the cell
            return false;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );

    // Handle Backspace key when table cells are selected
    const unregisterBackspace = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      () => {
        const selection = $getSelection();

        // Handle table selection (multiple cells selected)
        if ($isTableSelection(selection)) {
          $clearSelectedCells(selection);
          return true;
        }

        // For range selection within a single cell, let default behavior handle it
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const cellNode = $getTableCellNodeFromLexicalNode(anchorNode);

          if (cellNode) {
            // Let the default backspace behavior work within the cell
            return false;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      unregisterDelete();
      unregisterBackspace();
    };
  }, [editor]);

  // This plugin doesn't render anything - it just handles keyboard commands
  return null;
}

export default TableCellSelectionPlugin;
